import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Icon from 'components/Utils/NewIcon';
import UIUtils from 'services/UIUtils';
import PatientBadge from 'components/Patient/PatientBadge';
import SelectableListItem from 'components/Utils/SelectableListItem';
import './style.scss';
import PatientUtils from 'services/PatientUtils';
import DefaultReactSelect from 'components/Utils/DefaultReactSelect';
import RouteUtils from 'services/RouteUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';

let lastSearch;

const OptionClickContext = React.createContext(() => {});

/***
 * @param {any} Props
 */
const PatientSearchField = (props) => {
  const dispatch = useAppDispatch();
  const nav = useNavStack(Stacks.Master);

  const { onSelect } = props;

  const [results, setResults] = useState(props.defaultResults || []);
  const [typingTimeout, setTypingTimeout] = useState(undefined);
  const [search, setSearch] = useState(props.initialValue);
  const [clickedPatient, setClickedPatient] = useState(undefined);
  const [searching, setSearching] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (props.patient) setClickedPatient(props.patient);
  }, [props.patient]);

  useEffect(() => {
    if (!search && props.defaultResults) {
      setResults(props.defaultResults);
    }
  }, [props.defaultResults]);

  useEffect(
    () => {
      if (!!props.initialValue) searchPatients(props.initialValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.initialValue],
  );

  useEffect(() => {
    if (onSelect) {
      onSelect(clickedPatient);
      if (props.transient) {
        setSearch('');
        setResults(props.defaultResults || []);
      }
    }
  }, [clickedPatient, props.transient]);

  const handleSearchChange = useCallback(
    (value) => {
      clearTimeout(typingTimeout);
      setSearch(value);

      let timeoutHandle = setTimeout(async () => {
        if (value !== undefined && value !== '') await searchPatients(value);
      }, UIUtils.SEARCH_TYPE_TIMEOUT);

      setTypingTimeout(timeoutHandle);
    },
    [dispatch, typingTimeout],
  );

  async function searchPatients(value) {
    lastSearch = value;
    setSearching(true);
    const result = await dispatch(
      api.Patients.getPatients({
        search: (value ?? '').toLowerCase(),
        limit: 0,
      }),
    );
    if (result && lastSearch === value) {
      setResults(result ?? []);
    }
    setSearching(false);
  }

  function onPatientClick(patient) {
    if (patient) {
      let route = RouteUtils.getRoute(RouteUtils.routes().patient, {
        ':patientId': patient.id,
      });
      nav.push(route);
    }
  }

  let contentClass = 'search-field-content';
  if (clickedPatient) contentClass += ' has-patient';

  const options = useMemo(
    () =>
      results.map((patient) => {
        return {
          value: patient,
          label: PatientUtils.getName(patient),
        };
      }),
    [results],
  );

  const clearPatient = useCallback(() => {
    setClickedPatient(undefined);
  }, []);

  const onChange = useCallback((data) => {
    setClickedPatient(data.value);
  }, []);

  const filterOption = useCallback(() => true, []);

  const onClickHandler = useCallback((data) => {
    onChange(data);
    setMenuOpen(false);
  }, []);

  return (
    <OptionClickContext.Provider value={onClickHandler}>
      <div className="patient-search-field">
        <div className="search-field">
          {!props.hideTitle && <span className="small-title">{props.title || 'Patient'}</span>}
          <div className={contentClass}>
            {!props.transient && !!clickedPatient && (
              <>
                {!props.disabled && (
                  <Icon
                    src="xmark"
                    alt="Clear Patient"
                    showTooltip={true}
                    onClick={clearPatient}
                    className="clear-patient"
                  />
                )}
                <PatientBadge patient={clickedPatient} onClick={onPatientClick} />
              </>
            )}
            {(!clickedPatient || props.transient) && (
              <DefaultReactSelect
                className="patient-search"
                name="search"
                placeholder="Patient Search"
                inputValue={search || ''}
                menuIsOpen={menuOpen}
                setMenuOpen={setMenuOpen}
                filterOption={filterOption}
                loading={searching}
                noOptionsMessage="No patients found"
                onChange={onChange}
                onInputChange={handleSearchChange}
                {...(props.transient ? { value: null } : {})}
                options={options}
                components={{
                  Option: OptionComponent,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </OptionClickContext.Provider>
  );
};

const OptionComponent = ({ data, innerRef, isFocused }) => {
  const onClickHandler = React.useContext(OptionClickContext);
  return (
    <SelectableListItem
      key={data.value.id}
      divider={true}
      buttonRef={innerRef}
      selected={isFocused}
      special={isFocused}
      onClick={() => onClickHandler(data)}
      item={<PatientBadge patient={data.value} />}
    />
  );
};

PatientSearchField.propTypes = {
  onSelect: PropTypes.func,
  initialValue: PropTypes.string,
  patient: PropTypes.object,
  title: PropTypes.string,
  hideTitle: PropTypes.bool,
};

export default PatientSearchField;
